/**
 * Prospects slice - single source for prospects API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import { toast } from 'react-toastify';
import { PROSPECTS } from '../../api/endpoints';

export const getProspects = createAsyncThunk(
  'prospects/getProspects',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/prospects', { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch prospects');
    }
  }
);

export const getProspectById = createAsyncThunk(
  'prospects/getProspectById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(PROSPECTS.BY_ID(id));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Prospect not found');
    }
  }
);

export const createProspect = createAsyncThunk(
  'prospects/createProspect',
  async (prospectData, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/prospects', prospectData);
      toast.success('Prospect created successfully!');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create prospect');
    }
  }
);

export const updateProspect = createAsyncThunk(
  'prospects/updateProspect',
  async ({ id, prospectData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(PROSPECTS.BY_ID(id), prospectData);
      toast.success('Prospect updated successfully!');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update prospect');
    }
  }
);

export const deleteProspect = createAsyncThunk(
  'prospects/deleteProspect',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/prospects/${id}`);
      toast.success('Prospect deleted successfully!');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete prospect');
    }
  }
);

export const convertProspectToMerchant = createAsyncThunk(
  'prospects/convertProspectToMerchant',
  async ({ prospectId, merchantData }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(PROSPECTS.CONVERT(prospectId), merchantData);
      toast.success('Prospect converted to merchant successfully!');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to convert prospect');
    }
  }
);

export const getProspectStats = createAsyncThunk(
  'prospects/getProspectStats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get('/prospects/stats');
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch prospect stats');
    }
  }
);

export const searchProspects = createAsyncThunk(
  'prospects/searchProspects',
  async ({ query, filters = {} }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(PROSPECTS.SEARCH, {
        params: { query, ...filters },
      });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Search failed');
    }
  }
);

const initialState = {
  prospects: null,
  currentProspect: null,
  stats: null,
  loading: false,
  error: null,
};

const prospectsSlice = createSlice({
  name: 'prospects',
  initialState,
  reducers: {
    clearCurrentProspect: (state) => {
      state.currentProspect = null;
    },
    clearProspectsError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProspects.fulfilled, (state, action) => {
        state.loading = false;
        state.prospects = action.payload;
      })
      .addCase(getProspectById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProspect = action.payload;
      })
      .addCase(getProspectStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addMatcher(
        (a) =>
          [
            getProspects.pending,
            getProspectById.pending,
            createProspect.pending,
            updateProspect.pending,
            deleteProspect.pending,
          ].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) =>
          [
            getProspects.rejected,
            getProspectById.rejected,
            createProspect.rejected,
            updateProspect.rejected,
            deleteProspect.rejected,
          ].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      )
      .addMatcher(
        (a) =>
          [createProspect.fulfilled, updateProspect.fulfilled, deleteProspect.fulfilled].some((t) =>
            t.match(a)
          ),
        (state) => {
          state.loading = false;
        }
      );
  },
});

export const { clearCurrentProspect, clearProspectsError } = prospectsSlice.actions;
export default prospectsSlice.reducer;
